import {
  Header as TameeniHeader,
  HeaderLogo,
  HeaderSideNav,
  LanguageToggle,
  UserProfile,
} from "@tameeni/header";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/use-locale";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useConfig } from "@/hooks/use-config";
import { UserAccountActiveTabEnum } from "@/features/account-menu/helpers/constants";
import { useLogin } from "@/features/authentication";
import { useHeader } from "../hooks/use-header";
import { useAuth } from "react-oidc-context";
import {
  TameeniAppGallery,
  TameeniAppStore,
  TameeniGooglePlay,
} from "@/lib/constants";
import { trackNavigationMenu } from "@/lib/analytics/events";

export default function Header() {
  const config = useConfig();
  const locale = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, signOut } = useLogin();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { sideNavItems, socialLinks } = useHeader();
  const userProfileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = userProfileRef.current;
    if (!element) return;

    const handleClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('a[href*="/user-account"]')) {
        trackNavigationMenu({
          Product: "Travel",
          Selection: t("homepage.my-account"),
        });
      }
    };

    element.addEventListener("click", handleClick);
    return () => element.removeEventListener("click", handleClick);
  }, [t]);

  const handleSignIn = () => {
    const currentUrl =
      pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");
    signIn({ postLoginRedirectionUrl: currentUrl });
  };

  const onChangeLanguage = () => {
    const newLocale = locale === "ar" ? "en" : "ar";
    // Strip the current /:lng prefix before prepending the new one
    const pathWithoutLocale = pathname.startsWith(`/${locale}`)
      ? pathname.slice(`/${locale}`.length) || "/"
      : pathname;
    const qs = searchParams.toString();
    const queryString = qs ? `?${qs}` : "";
    navigate(`/${newLocale}${pathWithoutLocale}${queryString}`);
  };

  return (
    <TameeniHeader>
      <div className="box flex h-16 items-center gap-4">
        <HeaderSideNav
          androidAppUrl={TameeniGooglePlay}
          appGalleryUrl={TameeniAppGallery}
          direction={locale === "ar" ? "right" : "left"}
          downloadAppLabel={t("homepage.downloadApp")}
          followUsLabel={t("homepage.followTameeni")}
          iosAppUrl={TameeniAppStore}
          logo={locale === "ar" ? "TameeniLogoAR" : "TameeniLogoEN"}
          logoClassName={locale === "ar" ? "w-[113px]" : "w-[165px]"}
          sideNav={sideNavItems}
          socialLinks={socialLinks}
        />
        <div className="ms-0 grow md:ms-4">
          <HeaderLogo
            label={t("homepage.travel_insurance")}
            link={`${config.TAMEENI_HOME}/travel`}
            logoClassName={locale === "ar" ? "w-[113px]" : "w-[165px]"}
            mainLogo={locale === "ar" ? "TameeniLogoAR" : "TameeniLogoEN"}
          />
        </div>
        <LanguageToggle
          label={locale === "en" ? "العربية" : "English"}
          onLanguageChange={onChangeLanguage}
        />
        <div ref={userProfileRef}>
          <UserProfile
            isLoggedIn={isAuthenticated}
            loginLabel={t("homepage.login")}
            logoutLabel={t("homepage.logout")}
            onLogin={handleSignIn}
            onLogout={signOut}
            profileMenuItems={[
              {
                label: t("homepage.my-account"),
                link: `/${locale}/user-account?active-tab=${UserAccountActiveTabEnum.PURCHASED_POLICIES}&pageNumber=1&policy-status=1`,
                icon: "Settings",
              },
            ]}
            user={{
              name: user?.profile?.UserName as string,
              id: user?.profile?.IdentityNumber as string,
            }}
          />
        </div>
      </div>
    </TameeniHeader>
  );
}
